import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { REPORTS_V2 } from '../../../../shared/featureFlags/flagNames';
import { useParams } from '../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../helpers/features';
import ProjectGroupsModule from '../../../redux/project_grouping';
import { nuc } from '../../../utils';
import { isReleaseGroupScope, type ReportsV3Params } from '../types';
import type { ReportFormat } from '../../../../shared/reports';

type LoadPreview = {
  /**
   * URL to use as the iframe `key`. This forces the iframe to be reloaded when the URL changes.
   */
  cacheBustedURL: string;
  /**
   * Whether the iframe is currently loading.
   */
  isLoading: boolean;
  /**
   * Callback to be called when the iframe loads.
   */
  onIframeLoad: () => void;
};

/**
 * A simple hook for managing the loading state of the preview iframe.
 * Resets to "loading" whenever the preview URL changes.
 * Also manages cache busting in the preview URL, which we need to ensure that
 * when the preview URL changes, the iframe is actually reloaded.
 *
 * @param {string} previewURL - The URL of the preview iframe.
 */
export const useLoadPreview = (previewURL: string): LoadPreview => {
  const [isIframeLoaded, setIframeLoaded] = useState(false);

  const handleIframeLoad = useCallback(() => setIframeLoaded(true), []);

  //  Whenever the preview URL changes, we reset the iframe loaded state.
  useEffect(() => {
    setIframeLoaded(false);
  }, [previewURL]);

  // Cache busting. We add a timestamp to the URL to ensure that the iframe
  // is actually reloaded when the preview URL changes.
  const cacheBustedURL = useMemo(() => {
    return `${previewURL}${previewURL.includes('?') ? '&' : '?'}_cb=${Date.now()}`;
  }, [previewURL]);

  return {
    cacheBustedURL,
    isLoading: !isIframeLoaded,
    onIframeLoad: handleIframeLoad,
  };
};

type ProjectReportURL = `/api${'/v2' | ''}/revisions/${string}/attribution/preview?preview=true${string}`;
type ReleaseGroupReportURL = `/api${'/v2' | ''}/project_group/${number}/release/${number}/attribution/${ReportFormat}?preview=true${string}`;

/**
 * Determine the preview URL based on the URL params and other info.
 * TODO: later this will include all the format options, but for now
 * we don't include anything.
 */
export const usePreviewURL = (): ProjectReportURL | ReleaseGroupReportURL => {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  // Note: this will be removed at some point, maybe sooner than later,
  // because this page will eventually ONLY support v2. But in the meantime,
  // we want to test v1 and v2 in parallel until all the v2 reports are live.
  const isReportsV2Enabled = useIsFeatureEnabled(REPORTS_V2);
  const apiVersion = isReportsV2Enabled ? '/v2' : '';
  // Hardcoded temporarily for now.
  const reportFormat: ReportFormat = 'HTML';
  // The most reliable way to get the release ID is to use the selected release ID from Redux.
  // Ideally this would be like a query param but oh well
  const selectedReleaseId = useSelector(ProjectGroupsModule.selectors.getSelectedReleaseId);

  return useMemo<ProjectReportURL | ReleaseGroupReportURL>(() => {
    if (isReleaseGroup) {
      // Note that releaseId as a URL param part is not likely to actually exist for this path.
      // We'll try it (since we should really refactor Release Groups to be nested like that), but if it
      // doesn't exist, we'll use the selected release ID from Redux.

      //  Note that format is both a URL param and a query param.
      const { projectGroupId, releaseId } = params;
      return `/api${apiVersion}/project_group/${projectGroupId}/release/${releaseId ?? selectedReleaseId ?? 0}/attribution/${reportFormat}?preview=true&format=${reportFormat}`;
    }

    // URL for the project preview
    const projectLocator = nuc(`${params.project_id}$${params.revision_id}`);
    return `/api${apiVersion}/revisions/${projectLocator}/attribution/preview?preview=true&format=${reportFormat}`;
  }, [apiVersion, isReleaseGroup, params, reportFormat, selectedReleaseId]);
};
